/* eslint-disable jsx-a11y/media-has-caption */
"use client"
import type { HTMLProps } from "react"
import React, { useEffect, useRef, useState } from "react"
import type { VideoJsPlayerOptions, VideoJsPlayer } from "video.js"
import type { ChakraProps } from "@chakra-ui/react"
import { Box } from "@chakra-ui/react"
import type { ImageProps } from "next/image"
import Image from "next/image"
import "video.js/dist/video-js.css"
import { trackVideo } from "utils/gtm/video"
import { useSegment } from "tracking"
import { withErrorBoundary } from "react-error-boundary"
import { Logger } from "lib/logger"
import { removeNullField } from "tracking/utils/nullFields"
import { useInView } from "react-intersection-observer"
import debounce from "lodash.debounce"
import Head from "next/head"
import styles from "./VideoPlayer.legacy.module.css"
import { ExperimentFlags } from "@shared/utils.experimentation"

const videoStyles = Object.keys(styles)
  .map((t) => styles[t])
  .join(" ")

interface Props extends ChakraProps {
  coverImageProps: ImageProps
  coverImageContainerProps?: ChakraProps
  options: VideoJsPlayerOptions
  videoProps?: HTMLProps<HTMLVideoElement>
  videoPosition?: string | undefined
  autoPlayWhenScrolledIntoView?: boolean
  pauseWhenNotInView?: boolean
  muted?: boolean
  description?: string
}

interface PosterProps extends ChakraProps {
  imgProps?: ImageProps
}

const Poster: React.FC<PosterProps> = ({ imgProps, ...otherProps }) => (
  <Box
    position="absolute"
    top="0"
    left="0"
    bottom="0"
    right="0"
    w="100%"
    h="100%"
    cursor="pointer"
    {...otherProps}
  >
    {imgProps?.src && (
      <Image
        style={{ objectFit: imgProps?.style?.objectFit ?? "cover" }}
        title="Video Placeholder"
        {...imgProps}
        fill
        sizes="100vw"
      />
    )}
  </Box>
)

const VideoPlayerComponent: React.FC<Props> = ({
  options,
  videoProps,
  coverImageProps,
  coverImageContainerProps,
  videoPosition,
  autoPlayWhenScrolledIntoView = false,
  pauseWhenNotInView = false,
  muted = false,
  description,
  ...otherProps
}) => {
  const { track } = useSegment()

  const { ref, inView: autoplayInView } = useInView({
    triggerOnce: false,
    threshold: 0.75,
  })

  const { inView: preloadInView } = useInView({
    triggerOnce: true,
    rootMargin: "500px 0px 500px 0px", // Start preloading when within 500px of the viewport
    threshold: 0,
  })

  const [isVideoLoaded, setVideoLoaded] = useState(false)
  const [isCoverImageHidden, setCoverImageHidden] = useState(false)
  const [error, setError] = useState(null)
  const [isReady, setIsReady] = useState(false)
  const isAutoMuted = useRef(muted)
  const videoRef = useRef<HTMLVideoElement>(null)
  const videoPlayerRef = useRef<VideoJsPlayer>()
  const isProgrammaticPause = useRef<boolean>(false)
  const autoPlayExperiment = autoPlayWhenScrolledIntoView ? 1 : 0

  useEffect(() => {
    let player: VideoJsPlayer

    const loadVideo = async () => {
      const VideoJS = (await import("video.js")).default
      if (!videoRef.current) return undefined

      player = VideoJS(videoRef.current, {
        ...options,
        autoplay: false,
        preload: "metadata",
      })
      videoPlayerRef.current = player

      player.on("ready", () => {
        setIsReady(true)
        // We don't want the big button to flash before autoplay
        if (autoPlayWhenScrolledIntoView) {
          const bigPlayButton = player.getChild("BigPlayButton")
          if (bigPlayButton) {
            bigPlayButton.hide()
          }
        }
      })

      player.on("pause", () => {
        // after pause happens we do want to show the big play button
        if (autoPlayWhenScrolledIntoView) {
          const bigPlayButton = player.getChild("BigPlayButton")
          if (bigPlayButton) {
            bigPlayButton.show()
          }
        }
      })

      const onVolumeChange = debounce(() => {
        // do not track if the video is muted programmatically at the start
        if (isAutoMuted.current === true || !videoRef.current) return
        const { baseURI, title } = videoRef.current
        const volume = player.muted() ? 0 : player.volume()
        track({
          event: "Video Volume Changed",
          properties: {
            video_content_id: baseURI,
            video_content_title: title,
            video_position: videoPosition,
            volume,
          },
        })
      }, 250)

      player.on("volumechange", onVolumeChange)

      const onError = (playbackError) => {
        setError(playbackError)
        setVideoLoaded(false)
        track({
          event: "Video Playback Failed",
          properties: {
            video_content_id: videoRef.current?.baseURI ?? "",
            total_length: videoRef.current?.duration || 0,
            video_content_title: videoRef.current?.title ?? "",
            video_position: videoPosition,
            error: playbackError.target.innerText,
          },
        })
      }
      player.on("error", onError)

      setVideoLoaded(true)
    }

    loadVideo()
    return () => player?.dispose()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    const player = videoPlayerRef.current
    if (!player || !isReady) return undefined

    player.muted(muted)

    if (
      autoPlayWhenScrolledIntoView &&
      autoplayInView &&
      !isProgrammaticPause.current
    ) {
      player.play()
    } else if (pauseWhenNotInView && !autoplayInView) {
      player.pause()
    }
  }, [
    isReady,
    muted,
    autoPlayWhenScrolledIntoView,
    pauseWhenNotInView,
    autoplayInView,
    videoPlayerRef,
  ])

  useEffect(() => {
    const player = videoPlayerRef.current
    if (preloadInView && isReady && player) {
      if (player.preload() !== "auto") {
        player.preload(true)
        player.load()
      }
    }
  }, [preloadInView, isReady, videoPlayerRef])

  const schema = {
    "@context": "https://schema.org/",
    "@type": "VideoObject",
    name: videoProps?.title,
    description,
    thumbnailUrl: coverImageProps.src,
    duration: videoRef?.current?.duration,
    "@id": videoRef.current?.src,
  }
  return (
    <>
      <Head>
        <script
          type="application/ld+json"
          // eslint-disable-next-line react/no-danger
          dangerouslySetInnerHTML={{
            __html: JSON.stringify(schema),
          }}
        />
      </Head>
      {!error && (
        <Box
          {...otherProps}
          position="relative"
          display={isVideoLoaded ? "block" : "none"}
          className={videoStyles}
          ref={ref}
        >
          <div data-vjs-player>
            <video
              // eslint-disable-next-line react/jsx-props-no-spreading
              {...(typeof autoPlayExperiment !== "undefined" && {
                "data-event-intersection": "experimentViewed",
                "data-flag": ExperimentFlags.PAGE_EXPERIMENT_AUTOPLAY_VIDEO,
                "data-variation": autoPlayExperiment,
              })}
              ref={videoRef}
              crossOrigin="anonymous"
              preload="metadata"
              onPlay={() => {
                isAutoMuted.current = false
                isProgrammaticPause.current = false
                if (!isCoverImageHidden) setCoverImageHidden(true)
                trackVideo("play")
                if (videoRef.current && autoplayInView) {
                  const { duration, baseURI, title } = videoRef.current
                  track({
                    event: "Video Playback Started",
                    properties: {
                      total_length: removeNullField(duration),
                      video_content_id: baseURI,
                      video_content_title: title,
                      video_position: videoPosition,
                    },
                  })
                }
              }}
              onError={() => setCoverImageHidden(false)}
              onPause={() => {
                trackVideo("pause")
                if (autoplayInView) {
                  isProgrammaticPause.current = true
                }
                if (videoRef.current) {
                  const { duration, currentTime, baseURI, title } =
                    videoRef.current
                  const remaining = duration - currentTime
                  if (remaining >= 0.05) {
                    track({
                      event: "Video Playback Paused",
                      properties: {
                        total_length: removeNullField(duration),
                        video_content_id: baseURI,
                        video_content_title: title,
                        video_position: videoPosition,
                      },
                    })
                  }
                }
              }}
              onEnded={() => {
                trackVideo("completed")
                if (videoRef.current) {
                  const { duration, baseURI, title } = videoRef.current
                  track({
                    event: "Video Playback Completed",
                    properties: {
                      total_length: removeNullField(duration),
                      video_content_id: baseURI,
                      video_content_title: title,
                      video_position: videoPosition,
                    },
                  })
                }
              }}
              className="video-js vjs-big-play-centered"
              // eslint-disable-next-line react/jsx-props-no-spreading
              {...videoProps}
              style={{ objectFit: "cover", overflow: "hidden" }}
            />

            {!isCoverImageHidden && (
              <Poster
                imgProps={coverImageProps}
                {...coverImageContainerProps}
              />
            )}
          </div>
        </Box>
      )}

      {/* show this placeholder until the video is ready to play */}
      {!isVideoLoaded && (
        <Box
          {...otherProps}
          position="relative"
          css={{
            "&:after": {
              content: "''",
              display: "block",
              paddingBottom: "125%", // 4/5 aspect ratio
            },
          }}
        >
          <Poster imgProps={coverImageProps} {...coverImageContainerProps} />
        </Box>
      )}
    </>
  )
}

export const VideoPlayer = withErrorBoundary(VideoPlayerComponent, {
  fallback: <div />,
  onError(error) {
    Logger.warn(`VideoPlayer failed to load. ${error}`)
  },
})
